import type {
  ISQDCWorkflowDefinition,
  SQDCEventActionTypeInternalType,
  SQDCWorkflowDefinitionDataFragment,
  SQDCWorkflowEndBehavior as GraphQLSQDCWorkflowEndBehavior,
  SQDCWorkflowInstanceDataFragment,
  SQDCWorkflowNodeTransfer as GraphQLSQDCWorkflowNodeTransfer,
  SQDCWorkflowNodeType as GraphQLSQDCWorkflowNodeType,
  SQDCWorkflowFlagMode as GraphQLSQDCWorkflowFlagMode,
  SQDCWorkflowStartItemType as GraphQLSQDCWorkflowStartItemType,
  SQDCWorkflowVariableType as GraphQLSQDCWorkflowVariableType,
  SQDCActionStatus as GraphQLSQDCActionStatus,
} from "@/graphql";
import {
  ensureSQDCWorkflowStartNode,
  normalizeSQDCWorkflowLocalizedText,
  type SQDCWorkflowDefinition,
  type SQDCWorkflowInstance,
  type SQDCWorkflowLocalizedText,
} from "./domain";

export function toSQDCWorkflowDefinition(
  definition: SQDCWorkflowDefinitionDataFragment,
): SQDCWorkflowDefinition {
  const definitionWithInternalType = definition as SQDCWorkflowDefinitionDataFragment & {
    internalType?: SQDCEventActionTypeInternalType | null;
    internalTypeSiteId?: number | null;
  };
  return ensureSQDCWorkflowStartNode({
    id: definition.id,
    ready: definition.ready,
    usageCount: definition.usageCount,
    nameId: definition.nameId,
    name: toLocalizedText(readLanguage(definition, "name")),
    descriptionId: definition.descriptionId,
    description: toLocalizedText(readLanguage(definition, "description")),
    icon: definition.icon ?? undefined,
    internalType: definitionWithInternalType.internalType,
    internalTypeSiteId: definitionWithInternalType.internalTypeSiteId,
    boardIds: definition.BoardConnections.map((connection) => connection.boardId),
    siteIds: definition.SiteConnections.map((connection) => connection.siteId),
    variables: definition.Variables.map((variable) => ({
      id: variable.id,
      key: variable.key,
      type: variable.type,
    })),
    createdAt: definition.createdAt,
    updatedAt: definition.updatedAt,
    nodes: definition.nodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: {
        x: node.positionX,
        y: node.positionY,
      },
      startItemType: node.startItemType ?? undefined,
      eventActionTypeId: node.eventActionTypeId,
      shorthandId: node.shorthandId,
      transfer: node.transfer as SQDCWorkflowDefinition["nodes"][number]["transfer"],
      delegateBoardId: node.delegateBoardId,
      parentParallelNodeId: node.parentParallelNodeId ?? undefined,
      endBehavior: node.endBehavior,
      apiUrl: node.apiUrl ?? undefined,
      apiFetchOptions: node.apiFetchOptions ?? undefined,
      variableBindings: node.VariableBindings.map((binding) => ({
        id: binding.id,
        variableId: binding.variableId,
        fieldId: binding.fieldId,
        readonly: binding.readonly,
        read: binding.read,
        write: binding.write,
      })),
    })),
    connections: definition.connections.map((connection) => ({
      id: connection.id,
      sourceNodeId: connection.sourceNodeId,
      targetNodeId: connection.targetNodeId,
      scopeParallelNodeId: connection.scopeParallelNodeId ?? undefined,
      sortOrder: connection.sortOrder,
      previousActionStatus: connection.previousActionStatus as
        | SQDCWorkflowDefinition["connections"][number]["previousActionStatus"]
        | undefined,
      archiveEvent: connection.archiveEvent,
      requiresComment: connection.requiresComment,
      datapointTypeId: connection.datapointTypeId ?? undefined,
      flagMode: connection.flagMode ?? undefined,
      flagIds: connection.Flags.map(({ flagId }) => flagId),
      descriptionId: connection.descriptionId ?? undefined,
      description:
        readLanguage(connection, "description") == null
          ? undefined
          : toLocalizedText(readLanguage(connection, "description")),
      showDescriptionAsTooltip: connection.showDescriptionAsTooltip,
      autoProceed: connection.autoProceed,
      routingPoints: readRoutingPoints(connection.routingPoints),
      buttonLabelId: connection.buttonLabelId,
      buttonLabel:
        readLanguage(connection, "buttonLabel") == null
          ? undefined
          : toLocalizedText(readLanguage(connection, "buttonLabel")),
    })),
  });
}

export function toSQDCWorkflowDefinitionInput(
  definition: SQDCWorkflowDefinition,
): ISQDCWorkflowDefinition {
  const nodeById = new Map(definition.nodes.map((node) => [node.id, node]));
  return {
    name: normalizeSQDCWorkflowLocalizedText(definition.name),
    ready: definition.ready,
    description: normalizeSQDCWorkflowLocalizedText(definition.description),
    icon: definition.icon ?? undefined,
    boardIds: definition.boardIds,
    siteIds: definition.siteIds,
    variables: (definition.variables ?? []).map((variable) => ({
      ...variable,
      type: variable.type as GraphQLSQDCWorkflowVariableType,
    })),
    nodes: definition.nodes.map((node) => ({
      id: node.id,
      type: node.type as GraphQLSQDCWorkflowNodeType,
      positionX: node.position.x,
      positionY: node.position.y,
      startItemType: node.startItemType as GraphQLSQDCWorkflowStartItemType | undefined,
      eventActionTypeId: node.eventActionTypeId,
      shorthandId: node.shorthandId,
      transfer: node.transfer as GraphQLSQDCWorkflowNodeTransfer,
      delegateBoardId: node.delegateBoardId,
      parentParallelNodeId: node.parentParallelNodeId,
      endBehavior: node.endBehavior as GraphQLSQDCWorkflowEndBehavior | undefined,
      apiUrl: node.apiUrl?.trim() || undefined,
      apiFetchOptions: node.apiFetchOptions?.trim() || undefined,
      variableBindings: node.variableBindings ?? [],
    })),
    connections: definition.connections.map((connection) => ({
      id: connection.id,
      sourceNodeId: connection.sourceNodeId,
      targetNodeId: connection.targetNodeId,
      scopeParallelNodeId: connection.scopeParallelNodeId,
      sortOrder: connection.sortOrder,
      previousActionStatus: connection.previousActionStatus as GraphQLSQDCActionStatus | undefined,
      archiveEvent:
        ((nodeById.get(connection.sourceNodeId)?.type !== "START" &&
          nodeById.get(connection.targetNodeId)?.type === "EVENT") ||
          (nodeById.get(connection.sourceNodeId)?.type === "START" &&
            nodeById.get(connection.sourceNodeId)?.startItemType === "ACTION")) &&
        connection.archiveEvent === true,
      requiresComment: connection.requiresComment === true,
      datapointTypeId: connection.datapointTypeId,
      flagMode: connection.flagMode as GraphQLSQDCWorkflowFlagMode | undefined,
      flagIds: connection.flagIds ?? [],
      description: Object.values(normalizeSQDCWorkflowLocalizedText(connection.description)).some(
        (text) => typeof text === "string" && text.trim() !== "",
      )
        ? normalizeSQDCWorkflowLocalizedText(connection.description)
        : undefined,
      showDescriptionAsTooltip: connection.showDescriptionAsTooltip === true,
      autoProceed: connection.autoProceed === true,
      routingPoints: connection.routingPoints ?? [],
      buttonLabel: Object.values(normalizeSQDCWorkflowLocalizedText(connection.buttonLabel)).some(
        (label) => typeof label === "string" && label.trim() !== "",
      )
        ? normalizeSQDCWorkflowLocalizedText(connection.buttonLabel)
        : undefined,
    })),
  };
}

function readRoutingPoints(
  value: unknown,
): SQDCWorkflowDefinition["connections"][number]["routingPoints"] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((point) =>
    point != null &&
    typeof point === "object" &&
    "x" in point &&
    "y" in point &&
    typeof point.x === "number" &&
    typeof point.y === "number" &&
    Number.isFinite(point.x) &&
    Number.isFinite(point.y)
      ? [{ x: point.x, y: point.y }]
      : [],
  );
}

export function toSQDCWorkflowInstance(
  instance: SQDCWorkflowInstanceDataFragment,
): SQDCWorkflowInstance {
  return {
    workflowInstanceId: instance.workflowInstanceId,
    definitionId: instance.definitionId,
    definitionNameId: instance.Definition.nameId,
    definitionName: toLocalizedText(readLanguage(instance.Definition, "name")),
    status: instance.status,
    steps: instance.steps.map((step) => ({
      workflowStepId: step.workflowStepId,
      status: step.status,
      completedAt: step.completedAt ?? undefined,
      parallelBranchId: step.parallelBranchId ?? undefined,
      node: {
        id: step.node.id,
        type: step.node.type,
        position: {
          x: step.node.positionX,
          y: step.node.positionY,
        },
        startItemType: step.node.startItemType ?? undefined,
        eventActionTypeId: step.node.eventActionTypeId,
        shorthandId: step.node.shorthandId,
        transfer: step.node.transfer as SQDCWorkflowDefinition["nodes"][number]["transfer"],
        delegateBoardId: step.node.delegateBoardId,
        parentParallelNodeId: step.node.parentParallelNodeId ?? undefined,
        endBehavior: step.node.endBehavior,
        variableBindings: step.node.VariableBindings.map((binding) => ({
          id: binding.id,
          variableId: binding.variableId,
          fieldId: binding.fieldId,
          readonly: binding.readonly,
          read: binding.read,
          write: binding.write,
        })),
      },
      eventId: step.eventId,
      actionId: step.actionId,
      responsibleUserId: step.Action?.assigneeId ?? step.Event?.creatorId ?? undefined,
      createdAt: step.createdAt,
      completedConnectionId: step.completedConnectionId ?? undefined,
      completedButtonLabel: toLocalizedText(
        step.completedButtonLabel as Record<string, unknown> | undefined,
      ),
      completedDescription: toLocalizedText(
        step.completedDescription as Record<string, unknown> | undefined,
      ),
      completedDescriptionShownAsTooltip: step.completedDescriptionShownAsTooltip,
    })),
    eventIds: instance.Events.map((connection) => connection.eventId),
    actionIds: instance.Actions.map((connection) => connection.actionId),
    variableValues: Object.fromEntries(
      instance.VariableValues.map((variable) => [variable.variableId, variable.value]),
    ),
    parallelExecutions: instance.ParallelExecutions.map((execution) => ({
      id: execution.id,
      parallelNodeId: execution.parallelNodeId,
      parentBranchId: execution.parentBranchId ?? undefined,
      status: execution.status,
      completedAt: execution.completedAt ?? undefined,
      branches: execution.Branches.map((branch) => ({
        id: branch.id,
        parallelExecutionId: branch.parallelExecutionId,
        entryNodeId: branch.entryNodeId,
        status: branch.status,
        completedAt: branch.completedAt ?? undefined,
      })),
    })),
    history: instance.history.map((entry) => ({
      id: entry.id,
      at: entry.createdAt,
      type: entry.type,
      stepId: entry.workflowStepId,
      label: entry.label,
      completedConnectionId: entry.completedConnectionId ?? undefined,
      completedButtonLabel: toLocalizedText(
        entry.completedButtonLabel as Record<string, unknown> | undefined,
      ),
      completedDescription: toLocalizedText(
        entry.completedDescription as Record<string, unknown> | undefined,
      ),
      completedDescriptionShownAsTooltip: entry.completedDescriptionShownAsTooltip,
    })),
    createdAt: instance.createdAt,
    updatedAt: instance.updatedAt,
  };
}

function readLanguage(source: unknown, key: string) {
  const value = source as Record<string, unknown>;
  const capitalizedKey = `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  return (value[key] ?? value[capitalizedKey] ?? {}) as Record<string, unknown>;
}

function toLocalizedText(value: Record<string, unknown> | null | undefined) {
  return Object.fromEntries(
    Object.entries(value ?? {}).filter(
      ([key, translation]) =>
        key !== "__typename" &&
        ((key === "id" && typeof translation === "number") || typeof translation === "string"),
    ),
  ) as SQDCWorkflowLocalizedText;
}
